import React, { Component } from "react";
import { toast } from "react-toastify";
import { getOutlets, toggleOutlet, deleteOutlet } from "../../Endpoint/Admin/index";
import { ConsolePage } from "../../components/Console/ConsoleLayout";
import { C, Panel, Btn, Table, Chip, Toggle, IconBtn, ConfirmDialog, Thumb } from "../../components/Console/ConsoleUI";
import { canWrite, isBranchScopedAdmin } from "../../services/PermissionService";
import { KIND, windowSummary } from "./outletMeta";

/**
 * Restaurants & bars — the branch's outlets. Each can take table reservations
 * in time slots; the host stand opens an outlet's day view from here.
 *
 * Scope is the server's: a branch admin sees their branch's outlets, a global
 * admin the switcher's branch or all of them (with the branch named).
 */

class OutletList extends Component {
  constructor(props) {
    super(props);
    this.state = { isLoaded: false, outlets: [], togglingId: null, deleting: null, deleteBusy: false };
  }

  componentDidMount() {
    this.loadOutlets();
  }

  loadOutlets = () => {
    getOutlets().then((res) => {
      if (res && res.success) {
        this.setState({ isLoaded: true, outlets: res.data || [] });
      } else {
        this.setState({ isLoaded: true });
        toast.error((res && res.message) || "Could not load the outlets");
      }
    });
  };

  handleToggle = (o) => {
    this.setState({ togglingId: o._id });
    toggleOutlet(o._id, !o.active).then((res) => {
      this.setState({ togglingId: null });
      if (res && res.success) {
        toast.success(res.message);
        this.setState((prev) => ({
          outlets: prev.outlets.map((x) => (x._id === o._id ? { ...x, active: res.data.active } : x)),
        }));
      } else {
        toast.error((res && res.message) || "Could not update the outlet");
      }
    });
  };

  confirmDelete = () => {
    const o = this.state.deleting;
    this.setState({ deleteBusy: true });
    deleteOutlet(o._id).then((res) => {
      this.setState({ deleteBusy: false });
      if (res && res.success) {
        toast.success("Outlet deleted");
        this.setState((prev) => ({ deleting: null, outlets: prev.outlets.filter((x) => x._id !== o._id) }));
      } else {
        toast.error((res && res.message) || "Could not delete the outlet");
      }
    });
  };

  columns(write) {
    const showBranch = !isBranchScopedAdmin();
    return [
      {
        key: "name",
        label: "Outlet",
        render: (o) => {
          const kind = KIND[o.kind] || KIND.restaurant;
          return (
            <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
              <div style={{ flex: "0 0 44px" }}><Thumb src={o.image} icon={kind.icon} width={44} height={44} radius={10} /></div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 500, color: C.ink }}>{o.name}</div>
                <div style={{ marginTop: 3, fontSize: 13, color: C.taupeLight }}>
                  {kind.label}{o.cuisineLabel ? ` · ${o.cuisineLabel}` : ""}
                </div>
              </div>
            </div>
          );
        },
      },
      ...(showBranch
        ? [{
          key: "branch",
          label: "Branch",
          render: (o) => <span style={{ fontSize: 13, color: C.slate }}>{(o.branchId && (o.branchId.name || o.branchId.code)) || "—"}</span>,
        }]
        : []),
      {
        key: "reservations",
        label: "Reservations",
        render: (o) => {
          if (!o.acceptsReservations) return <span style={{ fontSize: 13, color: C.taupeLight }}>Walk-in only</span>;
          const r = o.reservation || {};
          const windows = r.windows || [];
          return (
            <div style={{ fontSize: 13, color: C.slate }}>
              {windows.slice(0, 2).map((w, i) => (
                <div key={i}>{w.name ? `${w.name}: ` : ""}{windowSummary(w)}</div>
              ))}
              {windows.length > 2 && <div style={{ color: C.taupeLight }}>+{windows.length - 2} more</div>}
              <div style={{ marginTop: 3, fontSize: 12, color: C.taupeLight }}>
                {r.slotMinutes || 30}-min slots · up to {r.maxPartySize || 12} guests
                {r.maxCoversPerSlot ? ` · ${r.maxCoversPerSlot} covers a slot` : ""}
              </div>
            </div>
          );
        },
      },
      {
        key: "active",
        label: "Status",
        width: 130,
        render: (o) => (
          <div style={{ display: "flex", alignItems: "center", gap: 10 }} onClick={(e) => e.stopPropagation()}>
            <Toggle
              on={o.active}
              disabled={!write || this.state.togglingId === o._id}
              title={o.active ? "Hide from the app" : "Show in the app"}
              onClick={() => write && this.handleToggle(o)}
            />
            <Chip tone={o.active ? "good" : "neutral"}>{o.active ? "Open" : "Off"}</Chip>
          </div>
        ),
      },
      {
        key: "actions",
        label: "",
        width: 130,
        render: (o) => (
          <div style={{ display: "flex", gap: 6 }} onClick={(e) => e.stopPropagation()}>
            {o.acceptsReservations && (
              <IconBtn icon="event_note" title="Today's bookings" onClick={() => this.props.history.push(`/outlets/${o._id}/bookings`)} />
            )}
            <IconBtn icon={write ? "edit" : "visibility"} title={write ? "Edit" : "Open"} onClick={() => this.openOutlet(o)} />
            {write && <IconBtn icon="delete" tone="bad" title="Delete" onClick={() => this.setState({ deleting: o })} />}
          </div>
        ),
      },
    ];
  }

  openOutlet = (o) => this.props.history.push(`/outlets/edit/${o._id}`);

  render() {
    const { isLoaded, outlets, deleting, deleteBusy } = this.state;
    const write = canWrite();
    const taking = outlets.filter((o) => o.active && o.acceptsReservations).length;

    return (
      <ConsolePage
        title="Restaurants & bars"
        subtitle={isLoaded ? `${outlets.length} outlet${outlets.length === 1 ? "" : "s"} · ${taking} taking reservations` : "Loading outlets…"}
        actions={
          write && (
            <Btn icon="add" onClick={() => this.props.history.push("/outlets/new")}>
              New outlet
            </Btn>
          )
        }
      >
        <Panel
          title="Outlets"
          subtitle="Guests book a time slot at an outlet that takes reservations. Tables not tied to an outlet stay bookable branch-wide, as before."
        >
          <div style={{ marginTop: 14 }}>
            {isLoaded ? (
              <Table
                columns={this.columns(write)}
                rows={outlets}
                onRowClick={this.openOutlet}
                empty="No outlets yet — add the branch's restaurant, bar or café"
              />
            ) : (
              <div style={{ padding: "50px 0", textAlign: "center", color: C.taupeLight }}>Loading outlets…</div>
            )}
          </div>
        </Panel>

        <ConfirmDialog
          isOpen={!!deleting}
          toggle={() => !deleteBusy && this.setState({ deleting: null })}
          onConfirm={this.confirmDelete}
          loading={deleteBusy}
          title={`Delete ${deleting ? deleting.name : "this outlet"}?`}
          body="Guests can no longer book it. Bookings already made keep their outlet and still show in the reservations list."
          confirmText="Delete outlet"
        />
      </ConsolePage>
    );
  }
}

export default OutletList;
